import { useCallback, useRef, useEffect } from 'react';

import { stringify } from 'qs';
import { useQueryParam, useRouterActions } from 'react-resource-router';

import {
  AVERAGE_DURATION_QUERY_PARAM,
  BRANCH_NAME_QUERY_PARAM,
  COMMIT_QUERY_PARAM,
  EXECUTED_AFTER_QUERY_PARAM,
  EXECUTED_BEFORE_QUERY_PARAM,
  FULLY_QUALIFIED_NAME_QUERY_PARAM,
  LABELS_QUERY_PARAM,
  PAGE_QUERY_PARAM,
  SORT_QUERY_PARAM,
  STATUS_QUERY_PARAM,
  TARGET_TYPE_QUERY_PARAM,
  TEST_TYPE,
} from '../constants';

type State = {
  sort?: string;
  pageIndex: number;
  branchName?: string;
  commit?: string;
  executedAfter?: string;
  executedBefore?: string;
  fullyQualifiedName?: string;
  labels?: string;
  status?: string;
  targetType?: string;
  testType?: string;
};

type Actions = {
  setPage: (newPage: number) => void;
  setQueryParam: (paramKey: string, newValue?: string) => void;
};

type Options = {
  updateMethod?: 'push' | 'replace';
};

type UseTestManagementQueryParams = [State, Actions];

export const useTestManagementQueryParams = (
  options?: Options
): UseTestManagementQueryParams => {
  const [averageDuration] = useQueryParam(AVERAGE_DURATION_QUERY_PARAM);
  const [branchName] = useQueryParam(BRANCH_NAME_QUERY_PARAM);
  const [commit] = useQueryParam(COMMIT_QUERY_PARAM);
  const [executedAfter] = useQueryParam(EXECUTED_AFTER_QUERY_PARAM);
  const [executedBefore] = useQueryParam(EXECUTED_BEFORE_QUERY_PARAM);
  const [fullyQualifiedName] = useQueryParam(FULLY_QUALIFIED_NAME_QUERY_PARAM);
  const [labels] = useQueryParam(LABELS_QUERY_PARAM);
  const [page, setPageParam] = useQueryParam(PAGE_QUERY_PARAM);
  const [sort] = useQueryParam(SORT_QUERY_PARAM);
  const [status] = useQueryParam(STATUS_QUERY_PARAM);
  const [targetType] = useQueryParam(TARGET_TYPE_QUERY_PARAM);
  const [testType] = useQueryParam(TEST_TYPE);
  const { push, replace } = useRouterActions();
  const updateType: 'push' | 'replace' =
    options?.updateMethod === 'replace' ? 'replace' : 'push';
  const navigate = updateType === 'replace' ? replace : push;

  const pageIndex = Number(page ?? 1) - 1;

  const setQueryParams = useCallback(
    (queryParams: Record<string, string | undefined>) => {
      const queryString = stringify(queryParams, {
        skipNulls: true,
      });

      navigate(`?${queryString}`);
    },
    [navigate]
  );

  // Use ref to store current query parameters to avoid dependency array issues
  const currentQueryParamsRef = useRef<Record<string, string | undefined>>({
    [AVERAGE_DURATION_QUERY_PARAM]: averageDuration,
    [BRANCH_NAME_QUERY_PARAM]: branchName,
    [COMMIT_QUERY_PARAM]: commit,
    [EXECUTED_AFTER_QUERY_PARAM]: executedAfter,
    [EXECUTED_BEFORE_QUERY_PARAM]: executedBefore,
    [FULLY_QUALIFIED_NAME_QUERY_PARAM]: fullyQualifiedName,
    [LABELS_QUERY_PARAM]: labels,
    [SORT_QUERY_PARAM]: sort,
    [STATUS_QUERY_PARAM]: status,
    [TARGET_TYPE_QUERY_PARAM]: targetType,
    [TEST_TYPE]: testType,
  });

  // Update ref whenever query parameters change
  useEffect(() => {
    currentQueryParamsRef.current = {
      [AVERAGE_DURATION_QUERY_PARAM]: averageDuration,
      [BRANCH_NAME_QUERY_PARAM]: branchName,
      [COMMIT_QUERY_PARAM]: commit,
      [EXECUTED_AFTER_QUERY_PARAM]: executedAfter,
      [EXECUTED_BEFORE_QUERY_PARAM]: executedBefore,
      [FULLY_QUALIFIED_NAME_QUERY_PARAM]: fullyQualifiedName,
      [LABELS_QUERY_PARAM]: labels,
      [SORT_QUERY_PARAM]: sort,
      [STATUS_QUERY_PARAM]: status,
      [TARGET_TYPE_QUERY_PARAM]: targetType,
      [TEST_TYPE]: testType,
    };
  }, [
    averageDuration,
    branchName,
    commit,
    executedAfter,
    executedBefore,
    fullyQualifiedName,
    labels,
    sort,
    status,
    targetType,
    testType,
  ]);

  const setPage = useCallback(
    (newPage: number) => {
      const newPageString = newPage.toString();
      if (newPageString === page) {
        return;
      }
      // changing the page param should preserve all other query params
      setPageParam(newPageString, updateType);
    },
    [page, setPageParam, updateType]
  );

  const setQueryParam = useCallback(
    (paramKey: string, newValue?: string) => {
      const currentValue = currentQueryParamsRef.current[paramKey];

      if (newValue === currentValue) {
        return;
      }

      setQueryParams({
        ...currentQueryParamsRef.current,
        [paramKey]: newValue,
      });
    },
    [setQueryParams]
  );

  return [
    {
      sort,
      pageIndex,
      branchName,
      commit,
      executedAfter,
      executedBefore,
      fullyQualifiedName,
      labels,
      status,
      targetType,
      testType,
    },
    {
      setPage,
      setQueryParam,
    },
  ];
};
